import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, jsonFetch } from '@/lib/api/jsonFetch'
import type {
  ItemPatch,
  ItemWrite,
  ItemsResponse,
  Pricelist,
  PricelistByPartner,
  PricelistItem,
  PricelistsResponse,
  PackagingsResponse,
} from './types'

const ROOT = '/api/pricelists'

/**
 * List customer-scoped pricelists. The `/customer` segment tells the
 * middleware to filter by the customer-vs-vendor distinction mechanism
 * chosen in Sprint 01 (documented in the sprint's "During-sprint
 * decisions"). Sprint 03 adds `/api/pricelists/vendor`.
 */
export function useCustomerPricelists(q: string = '') {
  return useQuery({
    queryKey: ['pricelists', 'customer', q],
    queryFn: () => {
      const params = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''
      return jsonFetch<PricelistsResponse>(`${ROOT}/customer${params}`)
    },
    staleTime: 30_000,
  })
}

/** Header + items for a single pricelist (used when navigating via URL). */
export function usePricelist(pricelistId: number | null) {
  return useQuery({
    queryKey: ['pricelists', 'detail', pricelistId],
    queryFn: () => jsonFetch<Pricelist>(`${ROOT}/${pricelistId}`),
    enabled: pricelistId !== null,
    staleTime: 15_000,
  })
}

/**
 * Resolve which pricelist a partner uses. Backs the `?partner=N`
 * deep-link on the Customer Pricelists page (Sprint 01 D-8).
 *
 * 404 is a legitimate state (the partner has no pricelist or doesn't
 * exist) — the UI handles it as a branch, not a transient error, so
 * we disable retry on 404.
 */
export function usePricelistByPartner(partnerId: number | null) {
  return useQuery({
    queryKey: ['pricelists', 'by-partner', partnerId],
    queryFn: () => jsonFetch<PricelistByPartner>(`${ROOT}/by-partner/${partnerId}`),
    enabled: partnerId !== null,
    staleTime: 30_000,
    retry: (_count, err) => !(err instanceof ApiError && err.status === 404),
  })
}

/** Items-only load for the EditableGrid. Separate from the detail query so
 *  the grid can invalidate/refetch without reloading the header. */
export function usePricelistItems(pricelistId: number | null) {
  return useQuery({
    queryKey: ['pricelists', 'items', pricelistId],
    queryFn: () => jsonFetch<ItemsResponse>(`${ROOT}/${pricelistId}/items`),
    enabled: pricelistId !== null,
    staleTime: 15_000,
  })
}

/**
 * Items for several pricelists at once, for the bulk-export CSV flow.
 * Fires one `GET .../items` request per id (reusing the same endpoint the
 * single-pricelist detail pane already calls) and returns the results in
 * the same order as `pricelistIds`. Capped by the caller (bulk-export cap)
 * before this ever runs, so this never fans out unbounded.
 */
export function useBulkPricelistItems(pricelistIds: readonly number[] | null) {
  const key = pricelistIds ? [...pricelistIds].sort((a, b) => a - b) : null
  return useQuery({
    queryKey: ['pricelists', 'items', 'bulk', key],
    queryFn: () =>
      Promise.all(
        (pricelistIds ?? []).map((id) => jsonFetch<ItemsResponse>(`${ROOT}/${id}/items`)),
      ),
    enabled: Boolean(pricelistIds && pricelistIds.length > 0),
  })
}

/**
 * Downloads a .zip of branded, customer-ready PDFs for several pricelists
 * at once (`POST /api/pricelists/export/pdf/bulk`). Uses a raw `fetch`
 * rather than `jsonFetch` because the response is a binary blob, not JSON
 * — mirrors `jsonFetch`'s auth/base-URL conventions (credentials included,
 * `VITE_API_BASE` prefix) so it behaves the same way in every environment.
 */
export async function downloadBulkPricelistPdfZip(pricelistIds: readonly number[]): Promise<void> {
  const API_BASE = import.meta.env.VITE_API_BASE ?? ''
  const res = await fetch(`${API_BASE}${ROOT}/export/pdf/bulk`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pricelist_ids: pricelistIds }),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new ApiError(res.status, text || `Bulk PDF export failed (${res.status})`)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `pricelists-${new Date().toISOString().slice(0, 10)}.zip`
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export interface CreateItemVars {
  pricelistId: number
  item: ItemWrite
}

export function useCreateItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: CreateItemVars) =>
      jsonFetch<PricelistItem>(`${ROOT}/${vars.pricelistId}/items`, {
        method: 'POST',
        body: vars.item,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['pricelists', 'items', vars.pricelistId],
      })
      void qc.invalidateQueries({ queryKey: ['pricelists', 'customer'] })
    },
  })
}

export interface PatchItemVars {
  itemId: number
  pricelistId: number
  patch: ItemPatch
}

export function usePatchItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: PatchItemVars) =>
      jsonFetch<PricelistItem>(`${ROOT}/items/${vars.itemId}`, {
        method: 'PATCH',
        body: vars.patch,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['pricelists', 'items', vars.pricelistId],
      })
    },
  })
}

export interface DeleteItemVars {
  itemId: number
  pricelistId: number
}

export function useDeleteItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: DeleteItemVars) =>
      jsonFetch<{ ok: true }>(`${ROOT}/items/${vars.itemId}`, {
        method: 'DELETE',
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['pricelists', 'items', vars.pricelistId],
      })
    },
  })
}

/** Pack sizes available for a product (by variant from the picker, or template). */
export function usePackagings(ref: { productId?: number | null; productTmplId?: number | null }) {
  const { productId, productTmplId } = ref
  return useQuery({
    queryKey: ['pricelists', 'packagings', productId ?? null, productTmplId ?? null],
    queryFn: () => {
      const qs = productId
        ? `product_id=${productId}`
        : `product_tmpl_id=${productTmplId}`
      return jsonFetch<PackagingsResponse>(`${ROOT}/search/packagings?${qs}`)
    },
    enabled: Boolean(productId || productTmplId),
    staleTime: 60_000,
  })
}
